import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import ContinuumBand from "@/components/ContinuumBand";
import StatusChip from "@/components/StatusChip";
import Logo from "@/components/Logo";

const DOMAINS = [
  { to: "/solutions/housing", label: "Housing, Homelessness & Basic Needs", status: "LIVE" },
  { to: "/solutions/mental-health", label: "Mental Health, Substance Use & Crisis", status: "IN DEVELOPMENT" },
  { to: "/solutions/aging-disability", label: "Disability, Aging & Continuing Care", status: "IN DEVELOPMENT" },
  { to: "/solutions/health-care", label: "Health-Care Systems", status: "IN DEVELOPMENT" },
];

const NAV = [
  { to: "/platform", label: "Platform" },
  { label: "Solutions", children: DOMAINS },
  { to: "/method", label: "Method" },
  { to: "/why", label: "Why" },
  { to: "/about", label: "About" },
];

function DesktopNav() {
  return (
    <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
      {NAV.map((item) =>
        item.children ? (
          <div key={item.label} className="relative group">
            <button
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#0A1224] transition-colors tracking-tight"
            >
              {item.label} <span aria-hidden className="text-slate-400">▾</span>
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 min-w-[360px] z-50">
              <div className="bg-white border border-slate-200 py-2">
                {item.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    data-testid={`nav-submenu-${c.to.replace(/\//g, "-")}`}
                    className="flex items-center justify-between gap-4 px-5 py-3 text-sm text-slate-700 hover:bg-[#F8F9FA] hover:text-[#0A1224] border-l-2 border-transparent hover:border-[#FF8A00]"
                  >
                    <span>{c.label}</span>
                    <StatusChip status={c.status} />
                  </NavLink>
                ))}
                <Link
                  to="/platform#service-landscape"
                  data-testid="nav-submenu-service-landscape"
                  className="flex items-center gap-2 px-5 py-3 mt-1 text-sm text-slate-500 hover:text-[#0A1224] border-t border-slate-200"
                >
                  Service Landscape &amp; Financial Flows — coming
                  <ArrowUpRight size={14} className="text-[#FF8A00]" />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium tracking-tight transition-colors ${
                isActive ? "text-[#0A1224]" : "text-slate-700 hover:text-[#0A1224]"
              }`
            }
          >
            {item.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

function MobileNav({ open, onClose }) {
  if (!open) return null;
  return (
    <div data-testid="mobile-nav" className="lg:hidden fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <Link to="/" onClick={onClose} className="flex items-center gap-2" data-testid="mobile-nav-logo">
          <Logo />
        </Link>
        <button
          data-testid="mobile-nav-close"
          aria-label="Close menu"
          onClick={onClose}
          className="p-2 text-[#0A1224]"
        >
          <X size={22} />
        </button>
      </div>
      <ul className="px-6 py-6 space-y-1">
        {NAV.flatMap((item) =>
          item.children
            ? [
                <li key={item.label} className="pt-4 pb-2 text-xs font-black uppercase tracking-[0.2em] text-slate-500">
                  {item.label}
                </li>,
                ...item.children.map((c) => (
                  <li key={c.to}>
                    <NavLink
                      to={c.to}
                      onClick={onClose}
                      data-testid={`mobile-nav-${c.to.replace(/\//g, "-")}`}
                      className="flex items-center justify-between gap-3 py-3 text-lg font-medium text-[#0A1224] border-b border-slate-100"
                    >
                      <span>{c.label}</span>
                      <StatusChip status={c.status} />
                    </NavLink>
                  </li>
                )),
              ]
            : [
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onClose}
                    data-testid={`mobile-nav-${item.to.replace(/\//g, "-")}`}
                    className="block py-3 text-lg font-medium text-[#0A1224] border-b border-slate-100"
                  >
                    {item.label}
                  </NavLink>
                </li>,
              ]
        )}
        <li className="pt-6">
          <Link
            to="/contact"
            onClick={onClose}
            data-testid="mobile-nav-contact-cta"
            className="block text-center bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3 hover:bg-[#FFA833]"
          >
            Book a walkthrough
          </Link>
        </li>
      </ul>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <ContinuumBand className="h-[3px]" testid="header-continuum-band" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="header-home-link" className="flex items-center">
            <Logo />
          </Link>
          <DesktopNav />
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-walkthrough-cta"
              className="bg-[#FF8A00] text-[#050A15] font-bold text-sm px-5 py-2.5 hover:bg-[#FFA833] transition-colors inline-flex items-center gap-2"
            >
              Book a walkthrough
              <ArrowUpRight size={16} />
            </Link>
          </div>
          <button
            data-testid="mobile-nav-toggle"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-[#0A1224]"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>
      <MobileNav open={open} onClose={() => setOpen(false)} />
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#0A1224] text-white pt-20 pb-10 mt-24 relative">
      <ContinuumBand className="absolute top-0 left-0 right-0 h-1" testid="footer-continuum-band" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
          <div className="col-span-2">
            <Logo dark className="mb-4" />
            <p className="text-slate-300 max-w-md leading-relaxed text-sm">
              One place-based evidence platform spanning the full continuum of care — housing,
              mental health, aging and disability, health care.
            </p>
            <p className="text-slate-400 text-xs mt-6 tracking-wide">
              A product of <span className="text-white">The Creative Coefficient</span>.
            </p>
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-4">Platform</div>
            <ul className="space-y-3 text-sm text-slate-300">
              <li><Link to="/platform" data-testid="footer-link-platform" className="hover:text-[#FF8A00]">Overview</Link></li>
              <li><Link to="/solutions/housing" data-testid="footer-link-housing" className="hover:text-[#FF8A00]">Housing, Homelessness &amp; Basic Needs</Link></li>
              <li><Link to="/solutions/mental-health" data-testid="footer-link-mental-health" className="hover:text-[#FF8A00]">Mental Health, Substance Use &amp; Crisis</Link></li>
              <li><Link to="/solutions/aging-disability" data-testid="footer-link-aging-disability" className="hover:text-[#FF8A00]">Disability, Aging &amp; Continuing Care</Link></li>
              <li><Link to="/solutions/health-care" data-testid="footer-link-health-care" className="hover:text-[#FF8A00]">Health-Care Systems</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-4">Company</div>
            <ul className="space-y-3 text-sm text-slate-300">
              <li><Link to="/why" data-testid="footer-link-why" className="hover:text-[#FF8A00]">Why Continuum</Link></li>
              <li><Link to="/method" data-testid="footer-link-method" className="hover:text-[#FF8A00]">Method</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" className="hover:text-[#FF8A00]">About</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#FF8A00]">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="text-xs text-slate-400 tabular-nums">
            © {new Date().getFullYear()} The Creative Coefficient · Continuum is built in Canada with Canadian-resident data.
          </div>
          <ul className="flex flex-wrap gap-6 text-xs text-slate-400">
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-white">Terms</Link></li>
            <li><Link to="/accessibility" data-testid="footer-link-accessibility" className="hover:text-white">Accessibility</Link></li>
            <li>
              <a
                href="https://geo-lens.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-link-geolens"
                className="hover:text-white inline-flex items-center gap-1"
              >
                GeoLens (consulting) <ArrowUpRight size={12} />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <>
      <Header />
      <main id="main" className="min-h-[60vh]">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}
